import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import LoginPage from "./components/LoginPage";
import AdminDashboard from "./components/AdminDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import MainLayout from "./components/MainLayout";
import { useAuth } from "./context/AuthContext";
import { FirebaseProvider } from "./context/FirebaseContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline, CircularProgress, Box } from "@mui/material";

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#4caf50",
    },
    background: {
      default: "#f5f5f5",
    },
  },
});

// Lazy load components that depend on Firebase
const ChallengesPage = React.lazy(() => import("./components/ChallengesPage"));
const ChallengeDetail = React.lazy(() =>
  import("./components/ChallengeDetail")
);
const AdminEditPage = React.lazy(() => import("./components/AdminEditPage"));
const AdminLoginsPage = React.lazy(() =>
  import("./components/AdminLoginsPage")
);

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
    <CircularProgress />
  </Box>
);

// Helper component to redirect based on auth status
function RootRedirect() {
  const auth = useAuth();

  if (!auth) {
    console.error(
      "Auth context is null in RootRedirect. Check Provider setup in index.js."
    );
    return <Navigate to="/login" replace />;
  }

  const { isAuthenticated, loading } = auth;

  if (loading) {
    return <LoadingFallback />;
  }

  return isAuthenticated ? (
    <Navigate to="/challenges" replace />
  ) : (
    <Navigate to="/login" replace />
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FirebaseProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* only challenge pages stay behind the username/passcode guard */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                {/* Wrapped in Suspense for better loading control */}
                <Route
                  path="/challenges"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ChallengesPage />
                    </Suspense>
                  }
                />
                {/* Challenge detail route */}
                <Route
                  path="/challenges/:challengeNumber"
                  element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ChallengeDetail />
                    </Suspense>
                  }
                />
                {/* Add other protected routes needing the MainLayout here */}
              </Route>
            </Route>

            {/* Admin pages handle their own Firebase auth */}
            <Route
              path="/admin"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDashboard />
                </Suspense>
              }
            />
            <Route
              path="/edit"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminEditPage />
                </Suspense>
              }
            />
            <Route
              path="/users"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminLoginsPage />
                </Suspense>
              }
            />

            {/* Redirect root path based on authentication */}
            <Route path="/" element={<RootRedirect />} />

            {/* Optional: Catch-all route for 404 or redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </FirebaseProvider>
    </ThemeProvider>
  );
}

export default App;
